import Player from '@vimeo/player';
import { createStore } from 'zustand';

// To prevent the player to be in a null state before initializing, the
// dummyplayer is it's default value
const dummyPlayer = new Proxy({} as Player, {
  get(target, prop) {
    if (typeof prop === 'string' && prop in target) {
      return target[prop as keyof Player];
    }

    return () => {
      console.warn(
        `Player is not initialized yet. Attemped to access ${String(prop)}`
      );
    };
  },
});

type VideoStoreState = {
  player: Player;
  root: HTMLDivElement | null;
  isLoaded: boolean;
  isPlaying: boolean;
  isMuted: boolean;
  isFullscreen: boolean;
  duration: number;
  seconds: number;
  isBuffering: boolean;
};

type VideoStoreActions = {
  onLoaded: (player: Player) => void;
  togglePlaying: () => void;
  toggleFullscreen: (isMobileDevice: boolean) => void;
  toggleMute: () => void;
  setTime: (percent: number) => void;
  setIsPlaying: (isPlaying: boolean) => void;
};

export type VideoStore = VideoStoreState & VideoStoreActions;

export const createVideoStore = (autoplay: boolean, muted: boolean) =>
  createStore<VideoStore>((set, get) => ({
    player: dummyPlayer,
    root: null,
    isLoaded: false,
    isPlaying: false,
    isFullscreen: false,
    isMuted: true,
    duration: 0,
    seconds: 0,
    isBuffering: false,

    onLoaded: (player) => {
      player.getDuration().then((duration) =>
        set({
          duration,
        })
      );

      if (!muted) {
        get().toggleMute();
      }

      set({
        isLoaded: true,
        isPlaying: autoplay,
      });
    },

    togglePlaying: () => {
      const { isPlaying, isBuffering } = get();

      if (isBuffering) return;

      if (isPlaying) {
        get().player.pause();
      } else {
        get()
          .player.play()
          .catch(() => {
            // Ignore "play() interrupted by pause()" errors - this is expected
            // when scrolling quickly or component unmounts during playback
          });
      }
      set({ isPlaying: !isPlaying });
    },
    toggleFullscreen: (isMobileDevice) => {
      const { isFullscreen } = get();

      if (isMobileDevice) {
        get().player.requestFullscreen();
      } else {
        const rootElement = get().root;
        if (!rootElement) return;

        if (!isFullscreen) {
          rootElement.requestFullscreen();
        } else {
          document.exitFullscreen();
        }

        set({ isFullscreen: !isFullscreen });
      }
    },

    toggleMute: () => {
      const { isMuted } = get();
      get().player.setMuted(!isMuted);
      set({ isMuted: !isMuted });
    },

    setTime: (percent) => {
      const { player, duration } = get();
      player.setCurrentTime(duration * percent);
    },

    setIsPlaying: (isPlaying) => {
      set({ isPlaying });

      if (isPlaying) {
        get()
          .player.play()
          .catch(() => {
            // Ignore "play() interrupted by pause()" errors - this is expected
            // when scrolling quickly or component unmounts during playback
          });
      } else {
        get().player.pause();
      }
    },
  }));
